import './EditSalesforceRelationshipModal.scss';
import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  accountKey,
  BillingAccount,
  SalesforceRelationship
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { AccountSelect } from './AccountSelect';
import { accountLabel, AccountOption } from './billing-utils';

interface EditSalesforceRelationshipModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (updated: SalesforceRelationship) => void;
  relationship: SalesforceRelationship;
  unmappedAccounts: BillingAccount[];
  allRelationships: SalesforceRelationship[];
}

export const EditSalesforceRelationshipModal = ({
  show,
  onClose,
  onSave,
  relationship,
  unmappedAccounts,
  allRelationships
}: EditSalesforceRelationshipModalProps) => {
  const [selectedAccounts, setSelectedAccounts] = useState<BillingAccount[]>(relationship.accounts);

  const accountOptions: AccountOption[] = useMemo(
    () =>
      [...relationship.accounts, ...unmappedAccounts].map(a => ({
        value: accountKey(a),
        label: accountLabel(a),
        account: a
      })),
    [relationship.accounts, unmappedAccounts]
  );

  const disabledAccounts = useMemo(
    () =>
      allRelationships
        .filter(r => r.id !== relationship.id)
        .flatMap(r => r.accounts.map(a => ({ account: a, mappedToRelName: r.name }))),
    [allRelationships, relationship.id]
  );

  const handleClose = useCallback(() => {
    setSelectedAccounts(relationship.accounts);
    onClose();
  }, [onClose, relationship.accounts]);

  // Disable Save when the mapping is unchanged so an unedited row can't be
  // staged as a (no-op) pending change.
  const isUnchanged = useMemo(() => {
    const before = new Set(relationship.accounts.map(accountKey));
    const after = selectedAccounts.map(accountKey);
    return before.size === after.length && after.every(key => before.has(key));
  }, [relationship.accounts, selectedAccounts]);

  const handleSave = useCallback(() => {
    // onSave is synchronous (stages changes); save errors surface via the parent's error dialog
    onSave({ ...relationship, accounts: selectedAccounts });
    onClose();
  }, [onClose, onSave, relationship, selectedAccounts]);

  return (
    <Modal show={show} onHide={handleClose} className='edit-sf-relationship-modal' centered>
      <Modal.Header closeButton>
        <div className='edit-sf-relationship-title-container'>
          <h2 className='edit-sf-relationship-title'>Map Accounts to {relationship.name}</h2>
          <p className='edit-sf-relationship-subtitle'>
            Salesforce Relationship ID: {relationship.id}
          </p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <AccountSelect
          options={accountOptions}
          value={selectedAccounts}
          onChange={setSelectedAccounts}
          disabledAccounts={disabledAccounts}
        />
        {/* raw <button> intentional: needs a custom link-style appearance  not in Button component */}
        {selectedAccounts.length > 0 && (
          <button type='button' className='sf-modal-clear' onClick={() => setSelectedAccounts([])}>
            Clear
          </button>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleClose}>
          Cancel
        </Button>
        <Button disabled={isUnchanged} onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
